/**
 * Presents workspace administration as one coherent, role-aware settings area.
 *
 * The shared header keeps workspace identity, administrator context, and
 * disabled feature tabs consistent so individual settings panels do not invent
 * their own navigation or imply authority the user does not have.
 */
import { Lock, ShieldCheck } from 'lucide-react'

import { cn } from '@/components/ui'

export interface WorkspaceSettingsTab {
  id: string
  label: string
  disabled?: boolean
  disabledReason?: string
}

interface WorkspaceSettingsHeaderProps {
  workspaceName: string
  applicationName: string
  workspaceId: string
  administrator: boolean
  tabs: WorkspaceSettingsTab[]
  activeTab: string
  onTabChange: (tab: string) => void
}

export function WorkspaceSettingsHeader({
  workspaceName,
  applicationName,
  workspaceId,
  administrator,
  tabs,
  activeTab,
  onTabChange,
}: WorkspaceSettingsHeaderProps) {
  // Security: Vault controls cross an authorization boundary. Keep workspace
  // identity and administrator status visible while the tabs change so the
  // actor can see exactly which source boundary they are configuring.
  return (
    <header>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.018em] text-slate-900">
            Workspace Settings
          </h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-slate-500">
            <span className="font-medium text-slate-700">{workspaceName}</span>
            <span aria-hidden="true">·</span>
            <span>{applicationName}</span>
            <span aria-hidden="true">·</span>
            <span className="font-mono">{workspaceId}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {administrator && (
            <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-slate-100 px-2.5 text-xs font-medium text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5" />
              Administrator
            </span>
          )}
        </div>
      </div>
      <nav className="mt-6 flex gap-6 overflow-x-auto border-b" aria-label="Workspace settings sections">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            disabled={tab.disabled}
            title={tab.disabledReason}
            aria-current={activeTab === tab.id ? 'page' : undefined}
            className={cn(
              '-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-0.5 pb-3 text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800',
              tab.disabled && 'cursor-not-allowed text-slate-300 hover:text-slate-300',
            )}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.disabled && <Lock className="h-3 w-3" aria-hidden="true" />}
            {tab.label}
          </button>
        ))}
      </nav>
    </header>
  )
}
